// Amazon order-history CSV. Mirrors scripts/loaders.py::load_amazon.
//
// The export Amazon currently offers (Order History Reports / Items)
// has the columns: Order ID, Order Date, Title, Item Total, Quantity,
// Currency, Status. Older "Orders" reports replace Item Total with
// "Total Charged" / "Total Owed" and Title with "Product Name". We
// accept either via forgiving column lookups.
//
// Sign: orders are always outflows → amount is positive (expense) and
// already matches the Item Total magnitude. No flip needed.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  parseDate,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

function pick(row: Record<string, string>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== "") return v;
  }
  return "";
}

export async function loadAmazon(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["Order"]);
  if (headerIdx < 0 || headerIdx >= rows.length - 1) return [];
  const headers = rows[headerIdx].map((h) => (h ?? "").trim());
  const dicts = toDictRows(rows[headerIdx], rows.slice(headerIdx + 1));

  // Raise if this isn't the Order-History shape we expect. The
  // amazon_settlement_flat_file_v2_sample.tsv ships `posted-date` /
  // `deposit-date` instead of `Order Date` — let it fall through to the
  // generic loader (processCsv catches our throw and retries).
  const hasOrderDate = headers.some(
    (h) => h.toLowerCase() === "order date" || h.toLowerCase() === "shipment date",
  );
  if (!hasOrderDate) {
    throw new Error(
      `Amazon parser couldn't find Order Date / Shipment Date column in ${filename} (got: ${headers.slice(0, 6).join(", ")}). Falling back to generic.`,
    );
  }

  const out: Transaction[] = [];
  for (const row of dicts) {
    // parseDate throws on empty / unrecognized — Amazon ships these in
    // partial / settlement TSVs. Skip the row instead of failing the
    // whole file; the processCsv generic-fallback handles the rest.
    let date: string;
    try {
      date = parseDate(pick(row, "Order Date", "Shipment Date"));
    } catch {
      continue;
    }
    if (!date) continue;
    const total = pick(row, "Item Total", "Total Charged", "Total Owed") || "0";
    const amount = parseAmount(total);
    const descRaw = pick(row, "Title", "Product Name", "Order ID") || "Amazon order";
    const description = descRaw.trim().slice(0, 120);
    const idHashInput = description.slice(0, 80); // Python uses [:80] for the id seed

    const id = await makeId("amazon", "amazon", date, amount, idHashInput);
    out.push(
      newTransaction({
        id,
        date,
        vendor_raw: `Amazon: ${description}`,
        vendor_key: "amazon",
        amount: Math.round(amount * 100) / 100,
        portal: "amazon",
        account: "amazon",
        account_kind: "debit",
        source_file: filename,
        raw: row,
      }),
    );
  }
  return out;
}
